/**
 * Report registry (spec 13, CONTENT.md section 10): ids, Indonesian titles, who may open
 * each report, its filters, whether it compares years, how it prints, and the file-name
 * stem. Metadata only, so the shell may read it (the nav, a card title) without carrying
 * a body: the bodies (dialect-neutral statements plus the TypeScript that turns their rows
 * into the table) live in ./queries and are reached through `@satu/shared/reports`
 * (./index.ts), which the screens, the device worker, the API and the print route import.
 *
 * Statistik Tahunan (section 15) is a definition like the others (./statistik-tahunan.ts,
 * registered in ./queries/index.ts): its rows are the numbered questions of the diocese's
 * form, its columns one per parish, so `GET /api/reports/:id`, the worker read, the print
 * route and the exports serve it unchanged; the screen at /laporan/statistik-tahunan reads
 * the same result and adds the source lines and the manual cells.
 */
import type { Peran } from '../schema/contract'
import ui from '../strings/ui.id.json' with { type: 'json' }

export type ReportId =
  | 'umat_per_lingkungan'
  | 'piramida_usia'
  | 'pendidikan_pekerjaan'
  | 'keluarga'
  | 'katekumen'
  | 'sakramen_per_tahun'
  | 'kelengkapan_data'
  | 'buku_agenda_surat'
  | 'statistik_tahunan'
  | 'profil_umat'

export type ReportFilter = 'tahun' | 'lingkungan'

export interface ReportDef {
  readonly id: ReportId
  readonly judul: string
  /** Where the figures come from: the device database or the server (spec 13.1, 13.2). */
  readonly cakupan: 'paroki' | 'keuskupan'
  /** Roles that may open the report; scoping (own lingkungan, own dekenat) is the caller's. */
  readonly peran: readonly Peran[]
  /** Needs register entries, which a ketua lingkungan device does not hold. */
  readonly membutuhkanRegister: boolean
  readonly filter: readonly ReportFilter[]
  /** Offers "Bandingkan dengan tahun" (CONTENT.md section 10). */
  readonly perbandinganTahun: boolean
  /** File name stem for xlsx and CSV exports, before `-{tahun}-{paroki}`. */
  readonly namaBerkas: string
  /** A4 orientation on `/cetak/laporan/:id`: wide tables lie down. */
  readonly cetak: 'portrait' | 'landscape'
}

// Release 32: the Pastor Paroki reads his parish's reports as its staff do.
const PAROKI: readonly Peran[] = ['staf_paroki', 'admin_paroki', 'pastor_paroki']
const KEUSKUPAN: readonly Peran[] = ['admin_keuskupan', 'uskup', 'dekan']
const PAROKI_DAN_KEUSKUPAN: readonly Peran[] = [...PAROKI, ...KEUSKUPAN]
const SEMUA: readonly Peran[] = [...PAROKI_DAN_KEUSKUPAN, 'ketua_lingkungan', 'ketua_stasi']

const judul = ui.laporan.kartu

export const REPORTS: readonly ReportDef[] = [
  {
    id: 'umat_per_lingkungan',
    judul: judul.umat_per_lingkungan,
    cakupan: 'paroki',
    peran: SEMUA,
    membutuhkanRegister: false,
    filter: ['tahun', 'lingkungan'],
    perbandinganTahun: true,
    // Sebutan unit terkecil: "Umat-per-Kombas" in a parish that says Kombas.
    namaBerkas: 'Umat-per-{unit}',
    cetak: 'landscape',
  },
  {
    id: 'piramida_usia',
    judul: judul.piramida_usia,
    cakupan: 'paroki',
    peran: PAROKI_DAN_KEUSKUPAN,
    membutuhkanRegister: false,
    filter: ['tahun', 'lingkungan'],
    perbandinganTahun: true,
    namaBerkas: 'Piramida-Usia',
    cetak: 'portrait',
  },
  {
    id: 'pendidikan_pekerjaan',
    judul: judul.pendidikan_pekerjaan,
    cakupan: 'paroki',
    peran: PAROKI_DAN_KEUSKUPAN,
    membutuhkanRegister: false,
    filter: ['tahun', 'lingkungan'],
    perbandinganTahun: true,
    namaBerkas: 'Pendidikan-dan-Pekerjaan',
    cetak: 'portrait',
  },
  {
    // Release 19 (owner, 2026-10-08): the parish data sheet's fields, today; the sensitive
    // sections for parish staff and admins only (`ParamLaporan.denganProfil`).
    id: 'profil_umat',
    judul: judul.profil_umat,
    cakupan: 'paroki',
    peran: SEMUA,
    membutuhkanRegister: false,
    filter: ['lingkungan'],
    perbandinganTahun: false,
    namaBerkas: 'Profil-Umat',
    cetak: 'portrait',
  },
  {
    id: 'keluarga',
    judul: judul.keluarga,
    cakupan: 'paroki',
    peran: SEMUA,
    membutuhkanRegister: false,
    filter: ['tahun', 'lingkungan'],
    perbandinganTahun: true,
    namaBerkas: 'Keluarga',
    cetak: 'portrait',
  },
  {
    id: 'katekumen',
    judul: judul.katekumen,
    cakupan: 'paroki',
    peran: SEMUA,
    membutuhkanRegister: false,
    filter: ['tahun', 'lingkungan'],
    perbandinganTahun: true,
    namaBerkas: 'Katekumen',
    cetak: 'portrait',
  },
  {
    id: 'sakramen_per_tahun',
    judul: judul.sakramen_per_tahun,
    cakupan: 'paroki',
    peran: PAROKI_DAN_KEUSKUPAN,
    membutuhkanRegister: true,
    filter: ['tahun'],
    perbandinganTahun: true,
    namaBerkas: 'Sakramen-per-Tahun',
    cetak: 'portrait',
  },
  {
    id: 'kelengkapan_data',
    judul: judul.kelengkapan_data,
    cakupan: 'paroki',
    peran: SEMUA,
    membutuhkanRegister: false,
    filter: ['lingkungan'],
    perbandinganTahun: false,
    namaBerkas: 'Kelengkapan-Data',
    cetak: 'landscape',
  },
  {
    id: 'buku_agenda_surat',
    judul: judul.buku_agenda_surat,
    cakupan: 'paroki',
    peran: PAROKI,
    membutuhkanRegister: false,
    filter: ['tahun'],
    perbandinganTahun: false,
    namaBerkas: 'Buku-Agenda-Surat',
    cetak: 'landscape',
  },
  {
    id: 'statistik_tahunan',
    judul: judul.statistik_tahunan,
    cakupan: 'keuskupan',
    // Diocese roles read every parish from the server; a parish role reads its own column
    // from the device (DISCUSSION-laporan.md). Staff enter the parish's typed figures there
    // (spec 2026-09-30-administrasi 6.2), so they open it too.
    peran: [...KEUSKUPAN, 'admin_paroki', 'staf_paroki', 'pastor_paroki'],
    membutuhkanRegister: true,
    filter: ['tahun'],
    perbandinganTahun: false,
    namaBerkas: 'Statistik-Tahunan',
    cetak: 'landscape',
  },
]

export function isReportId(value: unknown): value is ReportId {
  return typeof value === 'string' && REPORTS.some((r) => r.id === value)
}

export function reportById(id: ReportId): ReportDef {
  const found = REPORTS.find((r) => r.id === id)
  if (found === undefined) throw new Error(`unknown report ${id}`)
  return found
}

/** The report shelf for a role, in CONTENT.md order. */
export function reportsFor(peran: Peran): ReportDef[] {
  return REPORTS.filter((r) => r.peran.includes(peran))
}

/** Whether the year filter changes the table (Kelengkapan is present-tense). */
export function laporanTahunan(def: ReportDef): boolean {
  return def.filter.includes('tahun')
}
